import { Check, Plus, Search } from 'lucide-react';
import {
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { cx, useControllableState } from '../utils.ts';
import { Kbd } from './Kbd.tsx';
import './overlay.css';

export interface SearchItem {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Right-aligned secondary text. */
  hint?: ReactNode;
  /** Second line under the label. */
  description?: ReactNode;
  group?: string;
  /** Extra text that should match searches (aliases, field values…). */
  keywords?: string;
  disabled?: boolean;
  shortcut?: string;
}

export interface SearchListHandle {
  /** Forward key events from an external input (e.g. the rich-text editor). Returns true if handled. */
  onKeyDown(event: KeyboardEvent | ReactKeyboardEvent): boolean;
}

export interface SearchListProps {
  items: SearchItem[];
  onSelect: (item: SearchItem) => void;
  selectedIds?: ReadonlySet<string> | readonly string[];
  query?: string;
  onQueryChange?: (query: string) => void;
  placeholder?: string;
  emptyText?: ReactNode;
  onCreate?: (query: string) => void;
  createLabel?: (query: string) => ReactNode;
  autoFocus?: boolean;
  /** Filter `items` by the query (disable when the parent already filtered). */
  filter?: boolean;
  onEscape?: () => void;
  maxHeight?: number | string;
  showCheck?: boolean;
  hideInput?: boolean;
  footer?: ReactNode;
  limit?: number;
  className?: string;
  handleRef?: Ref<SearchListHandle>;
  /** Leading element inside the search row (e.g. a scope chip). */
  inputPrefix?: ReactNode;
}

export const foldText = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Rank how well `text` matches `query` (0 = no match). Prefix > word prefix > substring > subsequence. */
export function matchScore(text: string, query: string): number {
  if (!query) return 1;
  const t = foldText(text);
  const q = foldText(query.trim());
  if (!q) return 1;
  if (t === q) return 1000;
  if (t.startsWith(q)) return 900 - Math.min(t.length, 200);
  const words = t.split(/[\s\-_/.,:()]+/);
  if (words.some((w) => w.startsWith(q))) return 700 - Math.min(t.length, 200);
  const i = t.indexOf(q);
  if (i >= 0) return 500 - i;
  // Multi-word queries: every term must appear somewhere.
  const terms = q.split(/\s+/).filter(Boolean);
  if (terms.length > 1 && terms.every((term) => t.includes(term))) return 400;
  // Loose subsequence ("fbl" → "fireball"), but only when the letters stay close together;
  // otherwise short queries match half the project through letters scattered across words.
  if (q.length < 2) return 0;
  let ti = t.indexOf(q[0]!);
  if (ti < 0) return 0;
  let gaps = 0;
  for (const ch of q.slice(1)) {
    const found = t.indexOf(ch, ti + 1);
    if (found < 0) return 0;
    gaps += found - ti - 1;
    ti = found;
  }
  if (gaps > q.length * 2) return 0;
  return Math.max(1, 200 - gaps * 8);
}

export function SearchList({
  items,
  onSelect,
  selectedIds,
  query: queryProp,
  onQueryChange,
  placeholder = 'Search…',
  emptyText = 'No results',
  onCreate,
  createLabel,
  autoFocus = true,
  filter = true,
  onEscape,
  maxHeight = 320,
  showCheck,
  hideInput,
  footer,
  limit = 200,
  className,
  handleRef,
  inputPrefix,
}: SearchListProps) {
  const [query, setQuery] = useControllableState(queryProp, '', onQueryChange);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const baseId = useId();
  const selected = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);

  const visible = useMemo(() => {
    if (!filter || !query.trim()) return items.slice(0, limit);
    const scored = items
      .map((item, i) => ({ item, i, score: Math.max(matchScore(item.label, query), item.keywords ? matchScore(item.keywords, query) * 0.8 : 0) }))
      .filter((x) => x.score > 0);
    // Keep group order stable, rank inside each group.
    const groupRank = new Map<string, number>();
    items.forEach((it) => {
      const g = it.group ?? '';
      if (!groupRank.has(g)) groupRank.set(g, groupRank.size);
    });
    scored.sort((a, b) => groupRank.get(a.item.group ?? '')! - groupRank.get(b.item.group ?? '')! || b.score - a.score || a.i - b.i);
    return scored.slice(0, limit).map((x) => x.item);
  }, [items, query, filter, limit]);

  const trimmed = query.trim();
  const canCreate = !!onCreate && !!trimmed && !items.some((it) => foldText(it.label) === foldText(trimmed));
  const total = visible.length + (canCreate ? 1 : 0);

  useEffect(() => setActive(0), [query, items]);

  useLayoutEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const choose = (index: number) => {
    if (index < visible.length) {
      const item = visible[index]!;
      if (!item.disabled) onSelect(item);
    } else if (canCreate) {
      onCreate!(trimmed);
    }
  };

  const onKeyDown = (e: KeyboardEvent | ReactKeyboardEvent): boolean => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (total ? (a + 1) % total : 0));
      return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (total ? (a - 1 + total) % total : 0));
      return true;
    }
    if (e.key === 'Home' && hideInput) {
      setActive(0);
      return true;
    }
    if (e.key === 'End' && hideInput) {
      setActive(Math.max(0, total - 1));
      return true;
    }
    const composing = 'nativeEvent' in e ? e.nativeEvent.isComposing : e.isComposing;
    if (e.key === 'Enter' && !composing) {
      if (!total) return false;
      e.preventDefault();
      choose(Math.min(active, total - 1));
      return true;
    }
    if (e.key === 'Escape') {
      if (onEscape) {
        e.preventDefault();
        e.stopPropagation();
        onEscape();
        return true;
      }
    }
    return false;
  };

  useImperativeHandle(handleRef, () => ({ onKeyDown }));

  // Render with group headers.
  let lastGroup: string | undefined;
  const rows: ReactNode[] = [];
  visible.forEach((item, index) => {
    if (item.group && item.group !== lastGroup) {
      rows.push(
        <div key={`g-${item.group}-${index}`} className="g-list__group" role="presentation">
          {item.group}
        </div>,
      );
    }
    lastGroup = item.group;
    const isSelected = selected.has(item.id);
    rows.push(
      <div
        key={item.id}
        id={`${baseId}-${index}`}
        role="option"
        aria-selected={isSelected}
        aria-disabled={item.disabled || undefined}
        data-index={index}
        data-active={index === active || undefined}
        className="g-list__item"
        onMouseMove={() => active !== index && setActive(index)}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => choose(index)}
      >
        {showCheck && <span className="g-list__check">{isSelected && <Check size={14} />}</span>}
        {item.icon && <span className="g-list__icon">{item.icon}</span>}
        <span className="g-list__text">
          <span className="g-list__label">{item.label}</span>
          {item.description && <span className="g-list__desc">{item.description}</span>}
        </span>
        {item.hint && <span className="g-list__hint">{item.hint}</span>}
        {item.shortcut && <Kbd shortcut={item.shortcut} size="sm" />}
      </div>,
    );
  });
  if (canCreate) {
    const index = visible.length;
    rows.push(
      <div
        key="__create"
        id={`${baseId}-${index}`}
        role="option"
        aria-selected={false}
        data-index={index}
        data-active={index === active || undefined}
        className="g-list__item g-list__item--create"
        onMouseMove={() => active !== index && setActive(index)}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => choose(index)}
      >
        <span className="g-list__icon">
          <Plus size={15} />
        </span>
        <span className="g-list__text">
          <span className="g-list__label">{createLabel ? createLabel(trimmed) : <>Create “{trimmed}”</>}</span>
        </span>
      </div>,
    );
  }

  return (
    <div className={cx('g-list', className)}>
      {!hideInput && (
        <div className="g-list__search">
          <Search size={15} className="g-list__search-icon" />
          {inputPrefix}
          <input
            ref={inputRef}
            autoFocus={autoFocus}
            role="combobox"
            aria-expanded="true"
            aria-controls={`${baseId}-list`}
            aria-activedescendant={total ? `${baseId}-${Math.min(active, total - 1)}` : undefined}
            aria-autocomplete="list"
            placeholder={placeholder}
            value={query}
            spellCheck={false}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              onKeyDown(e);
            }}
          />
        </div>
      )}
      <div ref={listRef} id={`${baseId}-list`} role="listbox" className="g-list__items" style={{ maxHeight }}>
        {rows.length ? rows : <div className="g-list__empty">{emptyText}</div>}
      </div>
      {footer && <div className="g-list__footer">{footer}</div>}
    </div>
  );
}
